import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Prism from 'prismjs';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-markup-templating';
import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiCopy } from 'react-icons/fi';
import Editor from 'react-simple-code-editor';

type TLanguage = 'markup' | 'css' | 'javascript' | 'jsx';

type THtmlCodeEditorProps = {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  language?: TLanguage;
  minHeight?: number | string;
  maxHeight?: number | string;
  showHeader?: boolean;
  showFooter?: boolean;
  showLineNumbers?: boolean;
  showCopyButton?: boolean;
  fileName?: string;
  style?: CSSProperties;
  readOnly?: boolean;
};

// The theme's global `*` font-family rule targets every element directly (beating inheritance), so the code area
// must set monospace on itself and all descendants or the textarea caret drifts from the highlighted <pre>.
const MONO_FONT_FAMILY =
  '"Fira Code", "JetBrains Mono", "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const FILE_NAME_BY_LANGUAGE: Record<TLanguage, string> = {
  markup: 'index.html',
  css: 'styles.css',
  javascript: 'script.js',
  jsx: 'layout.jsx',
};

const LANGUAGE_LABEL: Record<TLanguage, string> = {
  markup: 'HTML',
  css: 'CSS',
  javascript: 'JavaScript',
  jsx: 'JSX',
};

const LANGUAGE_ACCENT: Record<TLanguage, string> = {
  markup: '#e34c26',
  css: '#264de4',
  javascript: '#f7df1e',
  jsx: '#61dafb',
};

export const HtmlCodeEditor: FC<THtmlCodeEditorProps> = ({
  value,
  onChange,
  placeholder,
  language = 'markup',
  minHeight = 200,
  maxHeight = 400,
  showHeader = true,
  showFooter = true,
  showLineNumbers = true,
  showCopyButton = true,
  fileName,
  style,
  readOnly = false,
}) => {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    [],
  );

  const highlight = useCallback(
    (code: string) => Prism.highlight(code, Prism.languages[language], language),
    [language],
  );

  const lineCount = Math.max(value.split('\n').length, 1);
  const charCount = value.length;
  const displayedFileName = fileName ?? FILE_NAME_BY_LANGUAGE[language];

  const handleCopy = useCallback(async () => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 1500);
    } catch {
      // Clipboard API unavailable — fail silently.
    }
  }, [value]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f6f8fa',
        color: '#24292f',
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid #d0d7de',
        boxShadow: '0 1px 2px rgba(31, 35, 40, 0.06), 0 4px 12px rgba(31, 35, 40, 0.06)',
        fontFamily: MONO_FONT_FAMILY,
        fontSize: 13,
        lineHeight: 1.65,
        // The textarea and the highlighted <pre> must lay out glyphs identically or the caret drifts.
        // Ligatures render differently once Prism splits them across token spans, so turn them off.
        fontVariantLigatures: 'none',
        fontKerning: 'none',
        letterSpacing: 0,
        // GitHub Light token palette — colors only: bold/italic tokens change glyph advance and misalign the caret.
        '& .token.comment, & .token.prolog, & .token.doctype, & .token.cdata': { color: '#6e7781' },
        '& .token.tag': { color: '#116329' },
        '& .token.tag > .token.punctuation, & .token.tag .token.tag > .token.punctuation': { color: '#6f42c1' },
        '& .token.attr-name': { color: '#953800' },
        '& .token.attr-value, & .token.string': { color: '#0a3069' },
        '& .token.punctuation': { color: '#24292f' },
        '& .token.keyword': { color: '#cf222e' },
        '& .token.boolean, & .token.number': { color: '#0550ae' },
        '& .token.function': { color: '#8250df' },
        '& .token.operator': { color: '#0550ae' },
        '& .token.selector': { color: '#116329' },
        '& .token.property': { color: '#0550ae' },
        '& .token.class-name': { color: '#953800' },
        '& .token.regex, & .token.important': { color: '#0a3069' },
        '& .token.entity, & .token.url, & .token.symbol': { color: '#24292f' },
        '& .token.variable': { color: '#953800' },
        '& .token.atrule': { color: '#cf222e' },
        '& .token.deleted': { color: '#82071e' },
        '& .token.inserted': { color: '#116329' },
        '&, & *': { fontFamily: MONO_FONT_FAMILY },
        // No soft-wrap: wrapped lines would desync the line-number gutter (and the caret) from the source lines.
        '& textarea, & pre': {
          whiteSpace: 'pre !important',
          wordBreak: 'normal !important',
          overflowWrap: 'normal !important',
          fontVariantLigatures: 'none',
          fontKerning: 'none',
          letterSpacing: 0,
        },
        '& textarea': {
          outline: 'none !important',
          caretColor: '#24292f',
        },
        '& textarea::selection': { background: '#b3d4fc7c' },
        '& textarea::placeholder': { color: '#8c959f', opacity: 1 },
        '& *': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#d0d7de transparent',
        },
        '& *::-webkit-scrollbar': { width: 10, height: 10 },
        '& *::-webkit-scrollbar-track': { background: 'transparent' },
        '& *::-webkit-scrollbar-thumb': {
          background: '#c1c8d1',
          borderRadius: 8,
          border: '2px solid #f6f8fa',
        },
        '& *::-webkit-scrollbar-thumb:hover': { background: '#afb8c1' },
        ...style,
      }}
    >
      {showHeader && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.75,
            py: 1,
            borderBottom: '1px solid #d0d7de',
            background: 'linear-gradient(180deg, #eef1f4 0%, #e4e8ec 100%)',
            userSelect: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
              {(['#ff5f56', '#ffbd2e', '#27c93f'] as const).map(color => (
                <Box
                  key={color}
                  sx={{
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    bgcolor: color,
                    boxShadow: 'inset 0 0 0 0.5px rgba(0, 0, 0, 0.18)',
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                fontSize: 11.5,
                color: '#57606a',
                fontFamily: 'inherit',
                fontWeight: 500,
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayedFileName}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.625,
                px: 0.875,
                py: 0.25,
                borderRadius: 0.75,
                fontSize: 10,
                fontFamily: 'inherit',
                fontWeight: 600,
                letterSpacing: 0.6,
                bgcolor: '#ffffff',
                color: '#57606a',
                border: '1px solid #d0d7de',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: LANGUAGE_ACCENT[language],
                  boxShadow: `0 0 6px ${LANGUAGE_ACCENT[language]}55`,
                }}
              />
              {LANGUAGE_LABEL[language]}
            </Box>

            {showCopyButton && (
              <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top" arrow>
                <IconButton
                  aria-label="Copy code"
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1,
                    color: copied ? '#1a7f37' : '#57606a',
                    transition: 'background-color 120ms ease, color 120ms ease',
                    '&:hover': {
                      bgcolor: 'rgba(31, 35, 40, 0.08)',
                      color: copied ? '#1a7f37' : '#24292f',
                    },
                    '&:focus-visible': { outline: '2px solid #0969da', outlineOffset: 1 },
                  }}
                >
                  {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          maxHeight,
          overflow: 'auto',
          scrollBehavior: 'smooth',
          bgcolor: '#f6f8fa',
        }}
      >
        {showLineNumbers && (
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              py: '16px',
              pl: '14px',
              pr: '12px',
              textAlign: 'right',
              color: '#8c959f',
              bgcolor: '#eef1f4',
              borderRight: '1px solid #dde3e8',
              userSelect: 'none',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              minWidth: 44,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {Array.from({ length: lineCount }, (_, index) => (
              <Box key={`line-${index + 1}`}>{index + 1}</Box>
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Editor
            value={value}
            onValueChange={onChange}
            highlight={highlight}
            padding={16}
            placeholder={placeholder}
            readOnly={readOnly}
            style={{
              minHeight,
              width: 'max-content',
              minWidth: '100%',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              background: 'transparent',
              color: 'inherit',
              whiteSpace: 'pre',
            }}
          />
        </Box>
      </Box>

      {showFooter && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.75,
            py: 0.5,
            borderTop: '1px solid #d0d7de',
            bgcolor: '#eef1f4',
            color: '#57606a',
            fontFamily: 'inherit',
            fontSize: 10.5,
            fontWeight: 500,
            letterSpacing: 0.4,
            userSelect: 'none',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>{lineCount} lines</Box>
            <Box>{charCount} chars</Box>
          </Box>
          <Box>UTF-8 · {LANGUAGE_LABEL[language]}</Box>
        </Box>
      )}
    </Box>
  );
};
